import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MoveCreationModal from '../MoveCreation/MoveCreationModal';
import { acceptMove, finishMove, fetchMoves, cancelMove } from '../../store/moves';
import { fetchTrailersUser } from '../../store/trailers';
import { useWebSocket } from '../../websocket/useWebSocket';

const MoveStatus = ({ onRefreshNeeded }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  
  // Initialize WebSocket connection
  const { isConnected } = useWebSocket();
  // Get the unfiltered moves from Redux store
  const allMoves = useSelector(state => state.moves.list || []);
  const sessionUser = useSelector(state => state.session.user);
  const selectedAccountId = useSelector(state => state.ui.selectedAccountId);
  const accounts = useSelector(state => Array.isArray(state.accounts) ? state.accounts : Object.values(state.accounts));
  const [processingMoves, setProcessingMoves] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // Create a local state for filtered moves
  const [filteredMoves, setFilteredMoves] = useState([]);
  // Create a local state for move counts
  const [moveCounts, setMoveCounts] = useState({
    requested: 0,
    inProgress: 0,
    finished: 0,
    cancelled: 0,
    dailyFinished: 0,
    dailyCancelled: 0
  });

  // Check if user is a manager or above
  const isManagerOrAbove = sessionUser && (
    sessionUser.is_admin ||
    sessionUser.is_manager ||
    sessionUser.is_account_manager ||
    sessionUser.is_super_manager
  );

  // Get the selected account name for display
  const selectedAccountName = selectedAccountId && accounts.length > 0
    ? accounts.find(account => account.id === selectedAccountId)?.account_name
    : null;

  // Function to check if user can act on a move
  const canActOnMove = (move) => {
    // Admin can act on any move, but when they've selected a specific account,
    // only show actions for that account's moves
    if (sessionUser?.is_admin) {
      if (selectedAccountId) {
        // When an account is selected, only allow actions on that account's moves
        return move.account_id === selectedAccountId;
      }
      // Otherwise admin can act on any move
      return true;
    }

    // Regular users can only act on moves for their own account
    return sessionUser?.account_id === move.account_id;
  };

  // Filter moves based on account and calculate counts
  useEffect(() => {
    if (isLoading) return; // Skip filtering until initial data load is complete

    let accountMoves = [];

    // Filter moves based on the selected account or user's account
    if (sessionUser?.is_admin && selectedAccountId) {
      accountMoves = allMoves.filter(move => move.account_id === selectedAccountId);
    } else if (sessionUser?.account_id) {
      accountMoves = allMoves.filter(move => move.account_id === sessionUser.account_id);
    } else {
      accountMoves = allMoves;
    }

    // Update local filtered moves state
    setFilteredMoves(accountMoves);

    // Calculate counts
    const counts = {
      requested: accountMoves.filter(move => !move.in_progress && !move.finish_time && !move.is_cancel).length,
      inProgress: accountMoves.filter(move => move.in_progress && !move.finish_time && !move.is_cancel).length,
      finished: accountMoves.filter(move => move.finish_time && !move.is_cancel).length,
      cancelled: accountMoves.filter(move => move.is_cancel).length,
      // Calculate daily counts from moves that were finished/cancelled today
      dailyFinished: accountMoves.filter(move => {
        if (!move.finish_time || move.is_cancel) return false;
        const finishDate = new Date(move.finish_time);
        const today = new Date();
        return finishDate.toDateString() === today.toDateString();
      }).length,
      dailyCancelled: accountMoves.filter(move => {
        if (!move.is_cancel) return false;
        const cancelDate = new Date(move.cancel_time);
        const today = new Date();
        return cancelDate.toDateString() === today.toDateString();
      }).length
    };

    setMoveCounts(counts);
  }, [allMoves, selectedAccountId, sessionUser, isLoading]);

  // Fetch moves when component mounts and set up periodic refresh
  useEffect(() => {
    // Set loading state at the beginning
    setIsLoading(true);

    // Initial fetch of both moves and trailers
    const fetchInitialData = async () => {
      try {
        if (sessionUser?.is_admin && selectedAccountId) {
          // Admin users with selected account
          await dispatch(fetchMoves(selectedAccountId));
        } else {
          // Regular users or admin without selected account
          await dispatch(fetchMoves());
        }

        // Also fetch trailers if user is logged in
        if (sessionUser && sessionUser.account_id) {
          await dispatch(fetchTrailersUser(sessionUser.account_id));
        }
      } catch (error) {
        console.error('Error fetching initial move data:', error);
      } finally {
        // Set loading to false after data is fetched
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [dispatch, sessionUser, selectedAccountId]);

  // Separate useEffect for polling that depends on filteredMoves
  useEffect(() => {
    if (isLoading) return; // Don't start polling until initial load is complete

    // Function to determine polling interval based on active moves
    const getPollingInterval = () => {
      const hasActiveMoves = filteredMoves.some(move => 
        move.in_progress || (!move.finish_time && !move.is_cancel)
      );
      return hasActiveMoves ? 15000 : 60000; // 15s if active moves, 60s if idle
    };

    // Set up polling with current interval
    const interval = setInterval(() => {
      // Refresh moves
      if (sessionUser?.is_admin && selectedAccountId) {
        dispatch(fetchMoves(selectedAccountId));
      } else {
        dispatch(fetchMoves());
      }

      // Also refresh trailers if user is logged in
      if (sessionUser && sessionUser.account_id) {
        dispatch(fetchTrailersUser(sessionUser.account_id));
      }
    }, getPollingInterval());

    return () => clearInterval(interval);
  }, [dispatch, sessionUser, selectedAccountId, filteredMoves, isLoading]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleStatusClick = (status) => {
    setSelectedStatus(status === selectedStatus ? null : status);
  };

  const handleAcceptMove = async (moveId) => {
    try {
      const updatedMove = await dispatch(acceptMove(moveId));
      if (updatedMove) {
        // FIX: Always fetch moves with correct account context
        if (sessionUser?.is_admin && selectedAccountId) {
          dispatch(fetchMoves(selectedAccountId));
        } else {
          dispatch(fetchMoves());
        }
      }
    } catch (error) {
      console.error('Failed to accept move:', error);
      // Show error notification to the user
      alert(error.message || 'Failed to accept move. It may have already been accepted or completed.');
    }
  };

  const handleFinishMove = async (moveId) => {
    try {
      // Finish the move
      const updatedMove = await dispatch(finishMove(moveId));

      if (updatedMove) {
        // FIX: Always fetch moves with correct account context
        if (sessionUser?.is_admin && selectedAccountId) {
          await dispatch(fetchMoves(selectedAccountId));
        } else {
          await dispatch(fetchMoves());
        }

        // Also explicitly refresh trailers data to show updated locations
        if (updatedMove.account_id) {
          await dispatch(fetchTrailersUser(updatedMove.account_id));
        } else if (sessionUser && sessionUser.account_id) {
          await dispatch(fetchTrailersUser(sessionUser.account_id));
        }

        // Refresh location history data to update durations
        if (onRefreshNeeded) {
          onRefreshNeeded();
        }

        // Toggle the selected status to force UI refresh
        const currentStatus = selectedStatus;
        setSelectedStatus(null);
        setTimeout(() => {
          setSelectedStatus(currentStatus);
        }, 100);
      }
    } catch (error) {
      console.error('Failed to finish move:', error);
      // Show error notification to the user
      alert(error.message || 'Failed to finish move. It may have already been completed or cancelled.');
    }
  };

  const handleCancelMove = async (moveId) => {
    try {
      const updatedMove = await dispatch(cancelMove(moveId));
      if (updatedMove) {
        // FIX: Always fetch moves with correct account context
        if (sessionUser?.is_admin && selectedAccountId) {
          dispatch(fetchMoves(selectedAccountId));
        } else {
          dispatch(fetchMoves());
        }
      }
    } catch (error) {
      console.error('Failed to cancel move:', error);
      // Show error notification to the user
      alert(error.message || 'Failed to cancel move. It may have already been completed or cancelled.');
    }
  };

  // Button click handlers with loading state
  const handleButtonAction = async (moveId, action) => {
    try {
      setProcessingMoves(prev => ({ ...prev, [moveId]: action }));

      let actionResult;
      switch (action) {
        case 'accept':
          actionResult = await dispatch(acceptMove(moveId));
          break;
        case 'finish':
          actionResult = await dispatch(finishMove(moveId));
          break;
        case 'cancel':
          actionResult = await dispatch(cancelMove(moveId));
          break;
        default:
          break;
      }

      if (actionResult) {
        // FIX: Always fetch moves with correct account context
        if (sessionUser?.is_admin && selectedAccountId) {
          dispatch(fetchMoves(selectedAccountId));
        } else {
          dispatch(fetchMoves());
        }

        // Also refresh trailers if user is logged in for all actions (accept, finish, cancel)
        if (sessionUser && sessionUser.account_id) {
          dispatch(fetchTrailersUser(sessionUser.account_id));
        }

        // Refresh location history for finish actions to update durations
        if (action === 'finish' && onRefreshNeeded) {
          onRefreshNeeded();
        }
      }
    } catch (error) {
      console.error(`Failed to ${action} move:`, error);

      // Show more user-friendly messages
      let errorMessage = `Error: Failed to ${action} the move`;

      if (error.message) {
        // Check for specific error conditions
        if (error.message.includes('already on the road') ||
            error.message.includes('already in use in another active move')) {
          errorMessage = error.message;
        } else if (error.message.includes('Permission denied')) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      }

      alert(errorMessage);
    } finally {
      setProcessingMoves(prev => {
        const updated = { ...prev };
        delete updated[moveId];
        return updated;
      });
    }
  };

  // Add this helper function to display the right permission message
  const getPermissionMessage = (move) => {
    if (sessionUser?.is_admin) {
      if (selectedAccountId && move.account_id !== selectedAccountId) {
        return `This move belongs to a different account (ID: ${move.account_id}). Select that account to modify this move.`;
      }
      return null; // Admin with no account selection or matching account can act on moves
    }

    // Regular users
    return "This move belongs to another account. You can only modify moves for your own account.";
  };

  const StatusCard = ({ title, count, color, status, dailyCount }) => {
    // Use the filtered moves instead of all moves
    const statusFilteredMoves = filteredMoves.filter(move => {
      switch (status) {
        case 'requested':
          return !move.in_progress && !move.finish_time && !move.is_cancel;
        case 'inProgress':
          return move.in_progress && !move.finish_time && !move.is_cancel;
        case 'finished':
          return move.finish_time && !move.is_cancel;
        case 'cancelled':
          return move.is_cancel;
        default:
          return false;
      }
    });

    return (
      <div className="flex-1">
        <div
          onClick={() => handleStatusClick(status)}
          className={`p-6 rounded-lg cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${color} mb-4`}
        >
          {dailyCount !== undefined ? (
            <div className="relative">
              <div className="text-2xl font-bold mb-1">{dailyCount}</div>
              <div className="text-md font-medium">{title}</div>
              <div className="absolute top-0 right-0 text-md text-gray-600 font-medium">
                Total: {count}
              </div>
            </div>
          ) : (
            <>
              <div className="text-2xl font-bold mb-1">{count}</div>
              <div className="text-md font-medium">{title}</div>
            </>
          )}
        </div>

        {selectedStatus === status && (
          <div className="bg-white rounded-lg shadow-lg p-4">
            <h3 className="text-lg font-semibold mb-4">
              {title} Details
            </h3>
            <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2">
              {statusFilteredMoves.map(move => (
                <div key={move.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">
                      Move #{move.id} - {move.move_details.type}
                    </span>
                    <div className="flex space-x-2">
                      {processingMoves[move.id] ? (
                        <div className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 text-2xl rounded">
                          <svg className="animate-spin mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing
                        </div>
                      ) : (
                        <>
                          {status === 'requested' && canActOnMove(move) && (
                            <>
                              <button
                                onClick={() => handleButtonAction(move.id, 'accept')}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-2xl rounded"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleButtonAction(move.id, 'cancel')}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-2xl rounded"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {status === 'inProgress' && canActOnMove(move) && (
                            <>
                              <button
                                onClick={() => handleButtonAction(move.id, 'finish')}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-2xl rounded"
                              >
                                Finish
                              </button>
                              <button
                                onClick={() => handleButtonAction(move.id, 'cancel')}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-2xl rounded"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {move.move_details.moves.map((m, index) => (
                      <div key={index} className="mb-1">
                        Move {index + 1}: {m.from} → {m.to}
                        {m.trailer && (
                          <span className="ml-2 text-gray-500">
                            (Trailer: {m.trailer})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Requested: {new Date(move.request_time).toLocaleString()}
                    {status === 'finished' && (
                      <>
                        <div className="mt-1">
                          Finished: {new Date(move.finish_time).toLocaleString()}
                        </div>
                        {move.elapse_time && (
                          <div className="mt-1">
                            Duration: {move.elapse_time}
                          </div>
                        )}
                      </>
                    )}
                    {!canActOnMove(move) && (
                      <div className="mt-2 text-sm text-red-500">
                        {getPermissionMessage(move)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="p-4 bg-white rounded-lg shadow mb-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold">Move Status</h2>
          </div>
          {isManagerOrAbove && (
            <button
              onClick={handleOpenModal}
              className="bg-blue-600 hover:bg-blue-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Create Move
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center p-10 min-h-[200px]">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
            <div className="ml-4 text-2xl font-semibold text-blue-600">Loading move data...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatusCard
              title="Requested"
              count={moveCounts.requested}
              color="bg-yellow-50 hover:bg-yellow-100"
              status="requested"
            />
            <StatusCard
              title="In Progress"
              count={moveCounts.inProgress}
              color="bg-green-50 hover:bg-green-100"
              status="inProgress"
            />
            <StatusCard
              title="Finished"
              count={moveCounts.finished}
              color="bg-blue-50 hover:bg-blue-100"
              status="finished"
              dailyCount={moveCounts.dailyFinished}
            />
            <StatusCard
              title="Cancelled"
              count={moveCounts.cancelled}
              color="bg-red-50 hover:bg-red-100"
              status="cancelled"
              dailyCount={moveCounts.dailyCancelled}
            />
          </div>
        )}
      </div>

      <MoveCreationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default MoveStatus;
